/**
 * Posting Rules — Phase 2 of native GL.
 *
 * Pure functions that translate operational events into balanced
 * journal-entry inputs. Side-effect free; the caller passes the result
 * to `postJournal()`. Idempotency is enforced by the auto-posting bridge
 * via stable `sourceRef.id` keys.
 *
 * Account mapping uses the seed CoA in `chartOfAccounts.ts`:
 *   1000 Cash — Operating         1100 AR                1200 WIP
 *   2000 AP                       2200 Deferred Revenue  4000 Revenue (parent)
 *   5000 COM (parent)             5500 COL (parent)
 * Trade-level children are resolved via `resolveTradeAccount()`.
 */
import type { Job, Payment, Invoice } from "@/data/types";
import type { ChangeOrder } from "@/data/changeOrderTypes";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { ReceivingRecord } from "@/data/WarehouseInventoryContext";
import type { JournalEntry } from "./glTypes";
import { SEED_CHART_OF_ACCOUNTS, DEFAULT_TRADE_TYPES, type TradeType } from "./chartOfAccounts";

/**
 * Canonical customer-id derivation from a free-text customer name.
 * Collapses internal whitespace + trims so "John  Smith" and "John Smith"
 * map to the same id (prevents AR-ledger collisions across modules).
 */
export function normalizeCustomerId(name: string | undefined | null): string {
  return (name ?? "").trim().replace(/\s+/g, "_").toLowerCase();
}

/** Map an opaque project type string to one of our seeded trade buckets. */
export function normalizeTrade(projectType: string | undefined | null): TradeType {
  if (!projectType) return "General";
  const s = projectType.toLowerCase();
  if (s.includes("bath")) return "Bathroom";
  if (s.includes("kitchen")) return "Kitchen";
  if (s.includes("floor")) return "Flooring";
  if (s.includes("window") || s.includes("door")) return "Windows";
  if (s.includes("roof")) return "Roofing";
  return "General";
}

/** Resolve a trade-level account id for a given parent + trade. */
export function resolveTradeAccount(
  parent: "revenue" | "com" | "col",
  trade: TradeType,
): string {
  const subtype =
    parent === "revenue" ? "revenue" :
    parent === "com" ? "cost_of_material" : "cost_of_labor";
  const match = SEED_CHART_OF_ACCOUNTS.find(a => a.subtype === subtype && a.tradeType === trade);
  if (match) return match.id;
  // Fall back to the "General" trade child — never to the rollup parent
  // (parents are non-postable per GL guardrail). Stable account numbers
  // for General are 4060 (revenue), 5060 (cost of material), 5560 (cost of labor).
  const generalMatch = SEED_CHART_OF_ACCOUNTS.find(a => a.subtype === subtype && a.tradeType === "General");
  return generalMatch?.id ?? (parent === "revenue" ? "4060" : parent === "com" ? "5060" : "5560");
}

// ─────────────────────────────────────────────────────────────────────────────
// Event → Journal Entry input
// ─────────────────────────────────────────────────────────────────────────────

export interface PostingRuleInput {
  postingDate: string;
  description: string;
  source: JournalEntry["source"];
  sourceRef: { kind: string; id: string };
  lines: Array<{
    accountId: string;
    side: "debit" | "credit";
    amount: number;
    memo?: string;
    customerId?: string;
    vendorId?: string;
    jobId?: string;
    tradeType?: string;
    wipBucket?: "material" | "labor";
  }>;
}

/** AR invoice issued — DR Accounts Receivable / CR Deferred Revenue (until job complete). */
export function ruleInvoiceIssued(job: Job, invoice: Invoice): PostingRuleInput | null {
  if (invoice.status === "draft" || !invoice.amount || invoice.amount <= 0) return null;
  const customerId = normalizeCustomerId(job.customerName);
  const trade = normalizeTrade(job.projectType);
  return {
    postingDate: (invoice.issuedAt ?? new Date().toISOString()).slice(0, 10),
    description: `Invoice ${invoice.id} — ${job.customerName} (${trade})`,
    source: "ar_invoice",
    sourceRef: { kind: "invoice", id: invoice.id },
    lines: [
      {
        accountId: "1100", side: "debit", amount: invoice.amount,
        customerId, jobId: job.id, memo: invoice.id, tradeType: trade,
      },
      {
        accountId: "2200", side: "credit", amount: invoice.amount,
        customerId, jobId: job.id, memo: `Deposit/progress until job complete`, tradeType: trade,
      },
    ],
  };
}

/** AR cash receipt — DR Cash / CR Accounts Receivable. */
export function rulePaymentReceived(job: Job, payment: Payment): PostingRuleInput | null {
  if (!payment.amount || payment.amount <= 0) return null;
  const customerId = normalizeCustomerId(job.customerName);
  const trade = normalizeTrade(job.projectType);
  const isCard = payment.method === "credit_card";
  // CC receipts land in Unreconciled Cash (1090) until matched by bank feed in Phase 4.
  const cashAccount = isCard ? "1090" : "1000";
  return {
    postingDate: payment.timestamp.slice(0, 10),
    description: `Payment ${payment.id} — ${job.customerName} (${payment.method})`,
    source: "ar_cash_receipt",
    sourceRef: { kind: "payment", id: payment.id },
    lines: [
      { accountId: cashAccount, side: "debit", amount: payment.amount, memo: payment.method, jobId: job.id },
      {
        accountId: "1100", side: "credit", amount: payment.amount,
        customerId, jobId: job.id, memo: payment.invoiceId, tradeType: trade,
      },
    ],
  };
}

/** Pay run released — DR WIP (labor to open job) / CR Cash — Payroll. */
export function rulePayRunReleased(job: Job | undefined, record: PayRunRecord): PostingRuleInput | null {
  const amount = record.netReleased;
  if (!amount || amount <= 0) return null;
  const trade = normalizeTrade(job?.projectType);
  return {
    postingDate: (record.releasedAt ?? new Date().toISOString()).slice(0, 10),
    description: `Pay run released — ${job?.customerName ?? record.jobId} (${trade} labor)`,
    source: "payroll",
    sourceRef: { kind: "pay_run", id: record.id },
    lines: [
      {
        accountId: "1200", side: "debit", amount,
        jobId: record.jobId, tradeType: trade, memo: "Direct labor to WIP",
      },
      { accountId: "1010", side: "credit", amount, memo: `Pay run ${record.id}` },
    ],
  };
}

/**
 * Job complete — recognize revenue + release WIP to COGS.
 *
 * - Revenue: DR Deferred Revenue / CR Revenue (trade).
 *   The caller passes `deferredRevenueBalance` = the amount currently sitting
 *   in 2200 for this job (sum of credits from issued invoices minus prior
 *   releases). We release exactly that — NOT contractValue — so the BS does
 *   not show a negative Deferred Revenue when billed-to-date < contract.
 *   Any remaining contract value is recognized when the final invoice posts.
 * - COGS release: caller passes the current WIP balance for the job + a
 *   material/labor split (resolved by the bridge from prior JE memos).
 */
export function ruleJobCompleted(
  job: Job,
  ctx: {
    wipMaterial: number;
    wipLabor: number;
    /** Live balance in 2200 for this job (preferred). */
    deferredRevenueBalance?: number;
    /** Legacy fallback when bridge cannot resolve the live balance. */
    contractValue?: number;
  },
): PostingRuleInput | null {
  const trade = normalizeTrade(job.projectType);
  const customerId = normalizeCustomerId(job.customerName);
  const lines: PostingRuleInput["lines"] = [];

  const releaseAmount = Math.max(
    0,
    Math.round((ctx.deferredRevenueBalance ?? ctx.contractValue ?? 0) * 100) / 100,
  );

  if (releaseAmount > 0) {
    lines.push({
      accountId: "2200", side: "debit", amount: releaseAmount,
      customerId, jobId: job.id, tradeType: trade, memo: "Release deposit",
    });
    lines.push({
      accountId: resolveTradeAccount("revenue", trade), side: "credit", amount: releaseAmount,
      jobId: job.id, tradeType: trade, memo: "Recognize revenue",
    });
  }
  if (ctx.wipMaterial > 0) {
    lines.push({
      accountId: resolveTradeAccount("com", trade), side: "debit", amount: ctx.wipMaterial,
      jobId: job.id, tradeType: trade, memo: "[material] to COGS",
    });
  }
  if (ctx.wipLabor > 0) {
    lines.push({
      accountId: resolveTradeAccount("col", trade), side: "debit", amount: ctx.wipLabor,
      jobId: job.id, tradeType: trade, memo: "[labor] to COGS",
    });
  }
  const wipTotal = ctx.wipMaterial + ctx.wipLabor;
  if (wipTotal > 0) {
    lines.push({
      accountId: "1200", side: "credit", amount: wipTotal,
      jobId: job.id, tradeType: trade, memo: "Release WIP",
    });
  }

  if (lines.length === 0) return null;
  return {
    postingDate: new Date().toISOString().slice(0, 10),
    description: `Job complete — ${job.customerName}: recognize revenue + release WIP (${trade})`,
    source: "wip_release",
    sourceRef: { kind: "job_complete", id: job.id },
    lines,
  };
}

/**
 * PO receipt → DR WIP (job) / CR GRNI (vendor).
 *
 * Posts at receive time so the BS reflects materials owned even before
 * the vendor bill arrives. When the bill is entered for the same PO,
 * APContext should debit 2150 GRNI to clear (manual today; the bill
 * line `glAccountId` can be set to "2150" by the user — guidance only).
 *
 * Amount = unit cost from the matching PO line × receivedQuantity.
 * Idempotency key = receiving-record id.
 */
export function ruleReceivedPO(
  po: PurchaseOrder,
  receipt: ReceivingRecord,
): PostingRuleInput | null {
  if (receipt.receivedQuantity <= 0) return null;
  if (receipt.status !== "Fully Received" && receipt.status !== "Partially Received") return null;
  const poLine = po.items.find(i => i.sku === receipt.sku);
  if (!poLine) return null;
  const amount = Math.round(poLine.unitCost * receipt.receivedQuantity * 100) / 100;
  if (amount <= 0) return null;
  const memo = `${po.poNumber} · ${receipt.sku} × ${receipt.receivedQuantity}`;
  return {
    postingDate: (receipt.receivedAt ?? new Date().toISOString()).slice(0, 10),
    description: `PO receipt — ${po.vendorName} → ${po.jobLabel}`,
    source: "ap_bill", // GRNI accrual stream
    sourceRef: { kind: "po_receipt", id: receipt.id },
    lines: [
      {
        accountId: "1200", side: "debit", amount,
        jobId: po.jobId, vendorId: po.vendorId, memo: `[material] ${memo}`,
        // wipBucket explicit so AutoPostingBridge release math is exact.
      },
      {
        accountId: "2150", side: "credit", amount,
        vendorId: po.vendorId, memo,
      },
    ],
  };
}

/**
 * Installer field-material reimbursement → DR WIP (material, job) / CR Cash — Operating.
 *
 * Posts once an installer's payout has actually paid out (paid_at set), for
 * the field-material-reimbursement portion of that payout's invoice — the
 * receipts a contractor/crew logged for materials bought in the field,
 * bundled into their payout invoice and released via the normal ACH payout
 * run (installer_payouts.status -> "paid").
 *
 * jobId comes straight from installer_payouts.execution_id, not a resolved
 * Job lookup — the WIP account requires a job subledger tag regardless of
 * whether the job happens to be loaded in local app state yet, so we tag
 * with the raw id and only use the resolved `job` (if available) for a
 * friendlier trade split and description.
 *
 * wipBucket is set explicitly (rather than relying on the "[material]" memo
 * tag alone) so AutoPostingBridge's WIP-release split at job completion
 * buckets this correctly as material cost, not labor.
 *
 * Idempotency key = payout id (one reimbursement JE per payout).
 */
export function ruleInstallerFieldMaterialReimbursement(
  payout: { id: string; execution_id: string | null; paid_at: string | null },
  fieldMaterialAmount: number,
  job?: Job,
): PostingRuleInput | null {
  const amount = Math.round((fieldMaterialAmount ?? 0) * 100) / 100;
  if (amount <= 0) return null;
  if (!payout.execution_id || !payout.paid_at) return null;
  const trade = normalizeTrade(job?.projectType);
  return {
    postingDate: payout.paid_at.slice(0, 10),
    description: `Field material reimbursement — ${job?.customerName ?? payout.execution_id} (${trade})`,
    source: "installer_reimbursement",
    sourceRef: { kind: "installer_payout_field_materials", id: payout.id },
    lines: [
      {
        accountId: "1200", side: "debit", amount,
        jobId: payout.execution_id, tradeType: trade, wipBucket: "material",
        memo: "[material] Field material reimbursement",
      },
      {
        accountId: "1000", side: "credit", amount,
        memo: `Installer payout ${payout.id} — field materials`,
      },
    ],
  };
}

/**
 * Approved/Signed change order → DR AR / CR Deferred Revenue for the
 * incremental amount. Recognized into revenue at job-completion sweep
 * along with the rest of the contract.
 *
 * Idempotency key = change-order id.
 */
export function ruleChangeOrderApproved(
  job: Job,
  co: ChangeOrder,
): PostingRuleInput | null {
  if (co.status !== "Signed") return null;
  const amt = Math.round((co.changeAmount ?? 0) * 100) / 100;
  if (amt === 0) return null;
  const customerId = normalizeCustomerId(job.customerName);
  const trade = normalizeTrade(job.projectType);
  // Positive CO = bill the customer more (DR AR / CR Deferred Rev).
  // Negative CO (credit) = reverse (CR AR / DR Deferred Rev).
  const positive = amt > 0;
  const abs = Math.abs(amt);
  return {
    postingDate: (co.signedAt ?? co.createdAt ?? new Date().toISOString()).slice(0, 10),
    description: `Change Order CO-${co.orderNumber} — ${job.customerName} (${trade})`,
    source: "ar_invoice",
    sourceRef: { kind: "change_order", id: co.id },
    lines: [
      {
        accountId: "1100", side: positive ? "debit" : "credit", amount: abs,
        customerId, jobId: job.id, tradeType: trade,
        memo: `CO-${co.orderNumber}: ${co.summary}`,
      },
      {
        accountId: "2200", side: positive ? "credit" : "debit", amount: abs,
        customerId, jobId: job.id, tradeType: trade,
        memo: `CO-${co.orderNumber} deferred until job complete`,
      },
    ],
  };
}
